import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Modal, View, FlatList, ActivityIndicator, Alert, Pressable, TextInput } from 'react-native';
import { Text, Title } from '@constants/Themed';
import { YStack, XStack, Card, ScrollView, SizableText, H4, Button, Spinner } from 'tamagui';
import { MaterialIcons, Ionicons } from '@expo/vector-icons';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import apis from '@api/api';
import useGlobalUser from '@hooks/getGlobalUser';
import styles from '@stylesheet';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { exportDataModelToHF } from '@api/api';

export interface HuggingFaceExportModalHandle {
  openModal: (dataModelId?: string) => void;
  closeModal: () => void;
}

interface HuggingFaceExportModalProps { }

const HuggingFaceExportModal = forwardRef<HuggingFaceExportModalHandle, HuggingFaceExportModalProps>((props, ref) => {
  const [visible, setVisible] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<string | null>(null);
  const [datasetName, setDatasetName] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const theme = themeSelector();
  const userId = useGlobalUser();
  const queryClient = useQueryClient();

  const colors = {
    background: theme ? '#ffffff' : '#1f2937',
    cardBackground: theme ? '#f8fafc' : '#374151',
    text: theme ? '#1f2937' : '#f9fafb',
    textSecondary: theme ? '#6b7280' : '#9ca3af',
    border: theme ? '#e5e7eb' : '#4b5563',
    accent: '#FFD21E',
    success: '#10b981',
  };

  // Fetch data models
  const { data: dataModels, isLoading: modelsLoading } = useQuery({
    queryKey: ['dataFolders', userId, 'all'],
    queryFn: async () => {
      return await apis.getDataFolders({
        userId,
        dataFilter: 'all'
      });
    },
    enabled: visible && !!userId,
  });

  // When modal opens with pre-selected model, fetch its details
  useEffect(() => {
    if (visible && selectedModelId && dataModels) {
      const model = dataModels.find((m: any) => m._id === selectedModelId);
      if (model && !datasetName) {
        const suggestedName = model.dataTitle
          ?.replace(/[^a-zA-Z0-9]/g, '_')
          .toLowerCase() || 'dataset';
        setDatasetName(suggestedName);
      }
    }
  }, [visible, selectedModelId, dataModels, datasetName]);

  // Mutation to export data model
  const exportMutation = useMutation({
    mutationFn: async ({ dataModelId, datasetName }: { dataModelId: string; datasetName: string }) => {
      return await apis.exportDataModelToHF(dataModelId, { datasetName });
    },
    onSuccess: (data) => {
      Alert.alert(
        'Export Started',
        'Your data model is being exported to Hugging Face. Training will begin automatically once the export is complete.',
        [{
          text: 'OK', onPress: () => {
            setVisible(false);
            resetState();
            queryClient.invalidateQueries({ queryKey: ['huggingFaceTrainingHistory'] });
          }
        }]
      );
    },
    onError: (error: any) => {
      Alert.alert(
        'Export Failed',
        error?.message || 'Failed to export data model. Please try again.',
        [{ text: 'OK' }]
      );
    }
  });

  useImperativeHandle(ref, () => ({
    openModal: (preSelectedModelId?: string) => {
      if (preSelectedModelId) {
        setSelectedModelId(preSelectedModelId);
        // Auto-fill dataset name based on model if available
        if (!datasetName && dataModels) {
          const model = dataModels.find((m: any) => m._id === preSelectedModelId);
          if (model?.dataTitle) {
            const suggestedName = model.dataTitle
              ?.replace(/[^a-zA-Z0-9]/g, '_')
              .toLowerCase() || 'dataset';
            setDatasetName(suggestedName);
          }
        }
      }
      setVisible(true);
    },
    closeModal: () => {
      setVisible(false);
      resetState();
    }
  }));

  const resetState = () => {
    setSelectedModelId(null);
    setDatasetName('');
    setSearchQuery('');
    setSubmitting(false);
  };

  const handleConfirm = async () => {
    if (!selectedModelId) {
      Alert.alert('Selection Required', 'Please select a data model to export.');
      return;
    }

    if (!datasetName.trim()) {
      Alert.alert('Name Required', 'Please enter a name for your Hugging Face dataset.');
      return;
    }

    setSubmitting(true);
    try {
      await exportMutation.mutateAsync({
        dataModelId: selectedModelId,
        datasetName: datasetName.trim()
      });
    } catch (error) {
      // Error handled in mutation
    } finally {
      setSubmitting(false);
    }
  };

  const filteredModels = (dataModels || []).filter((model: any) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      model.dataTitle?.toLowerCase().includes(query) ||
      model.dataTags?.some((tag: string) => tag.toLowerCase().includes(query))
    );
  });

  const selectedModel = dataModels?.find((m: any) => m._id === selectedModelId);

  const renderModelItem = ({ item }: { item: any }) => (
    <Pressable
      onPress={() => {
        setSelectedModelId(item._id);
        if (!datasetName) {
          // Auto-fill dataset name based on model title
          const suggestedName = item.dataTitle
            ?.replace(/[^a-zA-Z0-9]/g, '_')
            .toLowerCase() || 'dataset';
          setDatasetName(suggestedName);
        }
      }}
      style={[
        styles.defaultShadow,
        {
          marginVertical: 8,
          marginHorizontal: 4,
          borderRadius: 12,
          overflow: 'hidden'
        }
      ]}
    >
      <Card
        padding={16}
        backgroundColor={selectedModelId === item._id ? colors.accent : colors.cardBackground}
        borderRadius={12}
        borderWidth={2}
        borderColor={selectedModelId === item._id ? colors.accent : colors.border}
      >
        <XStack alignItems="center" gap={12}>
          <View
            style={{
              width: 48,
              height: 48,
              borderRadius: 24,
              backgroundColor: selectedModelId === item._id ? 'rgba(0,0,0,0.2)' : colors.accent,
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            <MaterialIcons
              name="folder"
              size={24}
              color={selectedModelId === item._id ? '#000000' : colors.cardBackground}
            />
          </View>
          <YStack flex={1}>
            <Text
              style={{
                fontSize: 16,
                fontWeight: '600',
                color: selectedModelId === item._id ? '#000000' : colors.text
              }}
            >
              {item.dataTitle?.replace(/_/g, ' ').toUpperCase() || 'Untitled Model'}
            </Text>
            <Text
              style={{
                fontSize: 12,
                color: selectedModelId === item._id ? 'rgba(0,0,0,0.7)' : colors.textSecondary,
                marginTop: 4
              }}
            >
              {item.count || 0} records • {item.dataTags?.join(', ') || 'No tags'}
            </Text>
          </YStack>
          {selectedModelId === item._id && (
            <Ionicons name="checkmark-circle" size={24} color="#000000" />
          )}
        </XStack>
      </Card>
    </Pressable>
  );

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={() => {
        setVisible(false);
        resetState();
      }}
    >
      <View
        style={{
          flex: 1,
          backgroundColor: 'rgba(0,0,0,0.5)',
          justifyContent: 'center',
          alignItems: 'center',
          padding: 20
        }}
      >
        <Card
          backgroundColor={colors.background}
          padding={24}
          borderRadius={16}
          width="100%"
          maxWidth={700}
          maxHeight="85%"
          shadowColor="#000"
          shadowOffset={{ width: 0, height: 4 }}
          shadowOpacity={0.3}
          shadowRadius={12}
          elevation={12}
        >
          {/* Header */}
          <XStack justifyContent="space-between" alignItems="center" marginBottom={20}>
            <YStack>
              <H4 style={{ color: colors.text, fontSize: 24, fontWeight: '700' }}>
                Export to Hugging Face
              </H4>
              <SizableText size="$3" color={colors.textSecondary} marginTop={4}>
                {selectedModelId ? 'Export this data model to Hugging Face' : 'Select a data model to export and train on Hugging Face'}
              </SizableText>
            </YStack>
            <Pressable onPress={() => {
              setVisible(false);
              resetState();
            }}>
              <Ionicons name="close" size={28} color={colors.textSecondary} />
            </Pressable>
          </XStack>

          {/* Search Bar - Only show if no model is pre-selected */}
          {!selectedModelId && (
            <View style={{ marginBottom: 16 }}>
              <TextInput
                value={searchQuery}
                onChangeText={setSearchQuery}
                placeholder="Search data models..."
                placeholderTextColor={colors.textSecondary}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  backgroundColor: colors.cardBackground,
                  color: colors.text,
                  fontSize: 14,
                  borderWidth: 1,
                  borderColor: colors.border
                }}
              />
            </View>
          )}

          {/* Data Models List - Only show if no model is pre-selected */}
          {!selectedModelId && (
            <View style={{ flex: 1, maxHeight: 300, marginBottom: 20 }}>
              {modelsLoading ? (
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                  <Spinner size="large" color={colors.accent} />
                  <SizableText size="$3" color={colors.textSecondary} marginTop={12}>
                    Loading data models...
                  </SizableText>
                </View>
              ) : filteredModels.length === 0 ? (
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                  <MaterialIcons name="folder-open" size={64} color={colors.textSecondary} />
                  <SizableText size="$4" color={colors.text} marginTop={12} fontWeight="600">
                    No Data Models Found
                  </SizableText>
                  <SizableText size="$3" color={colors.textSecondary} marginTop={8} textAlign="center">
                    Create a data model first to export to Hugging Face
                  </SizableText>
                </View>
              ) : (
                <FlatList
                  data={filteredModels}
                  renderItem={renderModelItem}
                  keyExtractor={(item) => item._id}
                  showsVerticalScrollIndicator={true}
                  contentContainerStyle={{ paddingBottom: 8 }}
                />
              )}
            </View>

          )}
          {selectedModel && (
            <Card
              backgroundColor={colors.cardBackground}
              padding={16}
              borderRadius={12}
              marginBottom={20}
              borderWidth={1}
              borderColor={colors.border}
            >
              <YStack gap={12}>
                <SizableText size="$3" color={colors.text} fontWeight="600">
                  Selected Model Preview
                </SizableText>
                <XStack gap={12}>
                  <View style={{
                    backgroundColor: colors.accent,
                    borderRadius: 8,
                    padding: 8,
                    width: 40,
                    height: 40,
                    justifyContent: 'center',
                    alignItems: 'center'
                  }}>
                    <MaterialIcons name="folder" size={20} color="#000000" />
                  </View>
                  <YStack flex={1}>
                    <SizableText size="$4" color={colors.text} fontWeight="600">
                      {selectedModel.dataTitle}
                    </SizableText>
                    <SizableText size="$2" color={colors.textSecondary} marginTop={4}>
                      {selectedModel.count || 0} records
                    </SizableText>
                    {selectedModel.description && (
                      <SizableText size="$2" color={colors.textSecondary} marginTop={4}>
                        {selectedModel.description}
                      </SizableText>
                    )}
                  </YStack>
                </XStack>
              </YStack>
            </Card>
          )}

          {/* Dataset Name Input */}
          {selectedModelId && (
            <YStack gap={8} marginBottom={20}>
              <SizableText size="$3" color={colors.text} fontWeight="600">
                Hugging Face Dataset Name
              </SizableText>
              <TextInput
                value={datasetName}
                onChangeText={setDatasetName}
                placeholder="Enter dataset name (e.g., my_custom_dataset)"
                placeholderTextColor={colors.textSecondary}
                style={{
                  padding: 12,
                  borderRadius: 8,
                  backgroundColor: colors.cardBackground,
                  color: colors.text,
                  fontSize: 14,
                  borderWidth: 1,
                  borderColor: colors.border
                }}
              />
              <SizableText size="$2" color={colors.textSecondary}>
                This name will be used for your dataset on Hugging Face. Use lowercase letters, numbers, and underscores only.
              </SizableText>
            </YStack>
          )}

          {/* Action Buttons */}
          <XStack gap={12}>
            <Button
              flex={1}
              backgroundColor={colors.cardBackground}
              color={colors.text}
              onPress={() => {
                setVisible(false);
                resetState();
              }}
              borderRadius={12}
              paddingVertical="$3"
              disabled={submitting}
            >
              <SizableText fontWeight="600" size="$4">
                Cancel
              </SizableText>
            </Button>
            <Button
              flex={1}
              backgroundColor={colors.accent}
              color="#000000"
              onPress={handleConfirm}
              disabled={!selectedModelId || !datasetName.trim() || submitting}
              borderRadius={12}
              paddingVertical="$3"
              opacity={(!selectedModelId || !datasetName.trim() || submitting) ? 0.5 : 1}
            >
              {submitting ? (
                <Spinner size="small" color="#000000" />
              ) : (
                <XStack alignItems="center" gap="$2">
                  <MaterialIcons name="upload" size={18} color="#000000" />
                  <SizableText color="#000000" fontWeight="600" size="$4">
                    Export & Train
                  </SizableText>
                </XStack>
              )}
            </Button>
          </XStack>
        </Card>
      </View>
    </Modal>
  );
}
);

HuggingFaceExportModal.displayName = 'HuggingFaceExportModal';

export default HuggingFaceExportModal;

